'use client';

import { useId } from 'react';
import { FileUp, X } from 'lucide-react';
import {
  TicketQuestion,
  questionLabel,
  sortQuestions,
  TICKET_DOCUMENT_ACCEPT,
  TICKET_DOCUMENT_FORMATS,
  TICKET_DOCUMENT_MAX_MB,
} from '@/lib/tickets/questions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useLanguage } from '@/lib/i18n/LanguageContext';

export interface QuestionFieldsProps {
  questions: TicketQuestion[];
  responses: Record<string, string>;
  files: Record<string, File>;
  /** question id → translation key */
  errors: Record<string, string>;
  /** An optional document with no upload session: explain and link to the event page. */
  uploadBlocked: boolean;
  eventUrl: string;
  onResponse: (questionId: string, value: string) => void;
  /** The size check is the parent's. */
  onFile: (questionId: string, file: File | null) => void;
}

export default function QuestionFields({
  questions,
  responses,
  files,
  errors,
  uploadBlocked,
  eventUrl,
  onResponse,
  onFile,
}: QuestionFieldsProps) {
  const { t, language } = useLanguage();
  const uid = useId();

  const questionPlaceholder = (question: TicketQuestion): string | undefined => {
    if (language === 'en' && question.placeholder_en) return question.placeholder_en;
    return question.placeholder_ua ?? undefined;
  };

  return (
    <>
      {sortQuestions(questions).map((question) => {
        const label = questionLabel(question, language);
        const errorKey = errors[question.id];
        const inputId = `${uid}-${question.id}`;
        const errorId = `${inputId}-error`;
        // No single control to point the label at: a choice group, or a document with no upload control.
        const grouped = ['single_choice', 'multiple_choice', 'true_false'].includes(question.field_type)
          || (question.field_type === 'file' && uploadBlocked);
        const errorMessage = errorKey ? t(errorKey, { size: String(TICKET_DOCUMENT_MAX_MB) }) : undefined;

        return (
          <div key={question.id} data-question-id={question.id}>
            {grouped ? (
              <span id={`${inputId}-label`} className="block text-sm font-medium text-gray-700 mb-2">
                {label}
                {question.is_required && <span className="text-red-600">*</span>}
              </span>
            ) : (
              <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-2">
                {label}
                {question.is_required && <span className="text-red-600">*</span>}
              </label>
            )}

            {/* Short Text */}
            {question.field_type === 'text' && (
              <Input
                id={inputId}
                type="text"
                value={responses[question.id] || ''}
                onChange={(e) => onResponse(question.id, e.target.value)}
                placeholder={questionPlaceholder(question)}
                error={errorMessage}
              />
            )}

            {/* Essay (Long Text) */}
            {question.field_type === 'essay' && (
              <Textarea
                id={inputId}
                value={responses[question.id] || ''}
                onChange={(e) => onResponse(question.id, e.target.value)}
                placeholder={questionPlaceholder(question)}
                rows={4}
                error={errorMessage}
              />
            )}

            {/* Single Choice (Radio) */}
            {question.field_type === 'single_choice' && (
              <div role="group" aria-labelledby={`${inputId}-label`} aria-describedby={errorMessage ? errorId : undefined} className="space-y-2">
                {question.field_options.map((option) => (
                  <label key={option} className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input
                      type="radio"
                      name={question.id}
                      value={option}
                      checked={responses[question.id] === option}
                      onChange={(e) => onResponse(question.id, e.target.value)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{option}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Multiple Choice (Checkbox) */}
            {question.field_type === 'multiple_choice' && (
              <div role="group" aria-labelledby={`${inputId}-label`} aria-describedby={errorMessage ? errorId : undefined} className="space-y-2">
                {question.field_options.map((option) => (
                  <label key={option} className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input
                      type="checkbox"
                      value={option}
                      checked={(responses[question.id] || '').split(',').includes(option)}
                      onChange={(e) => {
                        const currentValues = (responses[question.id] || '').split(',').filter(v => v);
                        const newValues = e.target.checked
                          ? [...currentValues, option]
                          : currentValues.filter(v => v !== option);
                        onResponse(question.id, newValues.join(','));
                      }}
                      className="h-4 w-4 rounded"
                    />
                    <span className="text-sm">{option}</span>
                  </label>
                ))}
              </div>
            )}

            {/* True/False */}
            {question.field_type === 'true_false' && (
              <div role="group" aria-labelledby={`${inputId}-label`} aria-describedby={errorMessage ? errorId : undefined} className="space-y-2">
                {(['true', 'false'] as const).map((value) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input
                      type="radio"
                      name={question.id}
                      value={value}
                      checked={responses[question.id] === value}
                      onChange={(e) => onResponse(question.id, e.target.value)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">
                      {t(value === 'true' ? 'ticketCustomFields.trueOption' : 'ticketCustomFields.falseOption')}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {/* File Upload */}
            {question.field_type === 'file' && (
              uploadBlocked ? (
                // Optional document, no session to upload with: say so plainly
                // and point at the full page rather than offer a control that
                // would fail, or hide the organizer's question altogether.
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs text-amber-900">{t('embed.documentUploadOptionalUnavailable')}</p>
                  <a
                    href={eventUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-blue-700 hover:underline"
                  >
                    {t('embed.completeOnConfy')}
                  </a>
                </div>
              ) : (
                <div>
                  {/* State the rules before a file is chosen, rather than
                      letting the bucket reject it after the fact. */}
                  <p className="text-xs text-gray-500 mb-2">
                    {t('ticketQuestions.fileRules', {
                      formats: TICKET_DOCUMENT_FORMATS,
                      size: String(TICKET_DOCUMENT_MAX_MB),
                    })}
                  </p>
                  <div className={`relative border-2 border-dashed rounded-lg p-6 text-center ${
                    errorMessage ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                  }`}>
                    <input
                      type="file"
                      id={inputId}
                      aria-invalid={errorMessage ? 'true' : undefined}
                      aria-describedby={errorMessage ? errorId : undefined}
                      accept={TICKET_DOCUMENT_ACCEPT}
                      onChange={(e) => onFile(question.id, e.target.files?.[0] || null)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <FileUp className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    {files[question.id] ? (
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm text-gray-700">{files[question.id].name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFile(question.id, null);
                          }}
                          className="text-red-600 hover:text-red-700 h-auto p-1"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">
                        {t('ticketCustomFields.clickToUpload')}
                      </p>
                    )}
                  </div>
                </div>
              )
            )}

            {/* Error Message - only for field types that don't have built-in error display */}
            {errorMessage && question.field_type !== 'text' && question.field_type !== 'essay' && (
              <p id={errorId} role="alert" className="text-sm text-red-600 mt-1">{errorMessage}</p>
            )}
          </div>
        );
      })}
    </>
  );
}
